import { Pencil, Trash2 } from 'lucide-react';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import { InterestsHoldPeriod } from '@/components/molecules/InterestsHoldIcon';
import PrintErrorMessage from '@/components/organisms/feedback/PrintErrorMessage';
import EditAdminModal from '@/components/macro-organisms/EditAdminModal';
import DialogConfirmDelete from '@/components/organisms/dialog/DialogConfirmDelete';
import InterestsHoldForm from './InterestsHoldForm';
import { formatHoldDate, getInterestsHoldStatus } from '@/utils/interestsHolds';

const STATUS_COLORS = {
    active: 'informativeForeground',
    upcoming: 'primaryStrong',
    past: 'disabled',
};

function ExistingHolds({ holds, onEdit, onDelete }) {
    if (!holds.length) return null;

    return (
        <div className="flex flex-col gap-betweenText px-thirdDegree pt-thirdDegree">
            <Label fontWeight="bold" message="consultants.statement.interestsHolds.existingHolds" />
            <div className="flex flex-col bg-white rounded-sm divide-y">
                {holds.map((hold) => {
                    const status = getInterestsHoldStatus(hold);
                    return (
                        <div key={hold.uuid} className="flex items-center justify-between gap-firstDegree px-firstDegree py-betweenText">
                            <div className="flex items-center gap-firstDegree grow">
                                <InterestsHoldPeriod hold={hold} />
                                <Label size="small" color={STATUS_COLORS[status]} message={`consultants.statement.interestsHolds.status_${status}`} />
                                {hold.comment && <Label size="small" color="disabled">{hold.comment}</Label>}
                            </div>
                            <Button style="ghost" sizePadding="small" showLeftIcon leftIcon={<Pencil className="h-4 w-4" />} showText={false} onClick={() => onEdit(hold)} />
                            <Button style="ghost" sizePadding="small" showLeftIcon leftIcon={<Trash2 className="h-4 w-4" />} showText={false} onClick={() => onDelete(hold)} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function InterestsHoldDialogs({ holdsState }) {
    const {
        t,
        canManageHolds,
        holds,
        error,
        mutationLoading,
        form,
        validateEndDate,
        isHoldModalOpen,
        editingHold,
        openEditHold,
        closeHoldModal,
        handleSubmitHold,
        holdToDelete,
        requestDeleteHold,
        closeDeleteHoldDialog,
        confirmDeleteHold,
    } = holdsState;

    if (!canManageHolds) return null;

    return (
        <>
            {isHoldModalOpen && (
                <EditAdminModal
                    data={editingHold}
                    onClose={closeHoldModal}
                    title={editingHold
                        ? 'consultants.statement.interestsHolds.edit'
                        : 'consultants.statement.interestsHolds.freeze'}
                    mediumSize={true}
                    form={
                        <div className="flex flex-col">
                            {error && <PrintErrorMessage error={error} />}
                            {!editingHold && <ExistingHolds holds={holds} onEdit={openEditHold} onDelete={requestDeleteHold} />}
                            <InterestsHoldForm form={form} validateEndDate={validateEndDate} t={t} />
                            {editingHold && (
                                <div className="flex justify-end px-thirdDegree pb-thirdDegree">
                                    <Button
                                        style="ghost"
                                        className="text-error"
                                        leftIcon={<Trash2 className="h-4 w-4" />}
                                        showLeftIcon
                                        text="consultants.statement.interestsHolds.delete"
                                        onClick={() => requestDeleteHold(editingHold)}
                                    />
                                </div>
                            )}
                        </div>
                    }
                    onSubmitForm={handleSubmitHold}
                    disabledSubmit={mutationLoading}
                />
            )}

            <DialogConfirmDelete
                open={!!holdToDelete}
                onClose={closeDeleteHoldDialog}
                onConfirm={confirmDeleteHold}
                title={holdToDelete
                    ? `${formatHoldDate(holdToDelete.startDate)} → ${holdToDelete.endDate ? formatHoldDate(holdToDelete.endDate) : t('consultants.statement.interestsHolds.indefinite')}`
                    : undefined}
            />
        </>
    );
}
